/**
 * CalendarFilterBar — the OPTIONAL city + tree-type filter chips for
 * {@link WorkshopCalendarPicker}. Rendered only when the picker's `enableFilters`
 * prop is on (it is OFF in the BON-202 cancel flow). This is a clean seam for
 * future call-sites (e.g. a browse-all-workshops view) — single-select chip rows
 * with an "All" reset; richer filtering (multi-select, persistence) is left to
 * those call-sites to flesh out.
 */
import type { CalendarFilterOption } from './calendarEvent';

export interface CalendarFilterBarProps {
  cityOptions: CalendarFilterOption[];
  treeOptions: CalendarFilterOption[];
  activeCityId: string | null;
  activeTreeId: string | null;
  onCityChange: (id: string | null) => void;
  onTreeChange: (id: string | null) => void;
}

export function CalendarFilterBar({
  cityOptions,
  treeOptions,
  activeCityId,
  activeTreeId,
  onCityChange,
  onTreeChange,
}: CalendarFilterBarProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
      <ChipRow
        ariaLabel="Filter by city"
        allLabel="All cities"
        options={cityOptions}
        activeId={activeCityId}
        onChange={onCityChange}
      />
      <ChipRow
        ariaLabel="Filter by tree type"
        allLabel="All trees"
        options={treeOptions}
        activeId={activeTreeId}
        onChange={onTreeChange}
      />
    </div>
  );
}

function ChipRow({
  ariaLabel,
  allLabel,
  options,
  activeId,
  onChange,
}: {
  ariaLabel: string;
  allLabel: string;
  options: CalendarFilterOption[];
  activeId: string | null;
  onChange: (id: string | null) => void;
}) {
  return (
    <div role="group" aria-label={ariaLabel} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      <Chip label={allLabel} active={activeId === null} onClick={() => onChange(null)} />
      {options.map((o) => (
        <Chip
          key={o.id}
          label={o.label}
          active={activeId === o.id}
          onClick={() => onChange(o.id)}
        />
      ))}
    </div>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className="nb-pill"
      style={{
        height: 22,
        fontSize: 11,
        padding: '0 9px',
        cursor: 'pointer',
        border: `1px solid ${active ? 'var(--nb-primary)' : 'var(--nb-border-2)'}`,
        background: active ? 'var(--nb-primary-soft, rgba(0,0,0,.05))' : 'var(--nb-bg)',
        color: 'var(--nb-ink)',
      }}
    >
      {label}
    </button>
  );
}
